import TableLoad from "components/load/TableLoad";
import { PageTitle, PageLayout, ContentContainer } from "components/layout";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "components/ui/use-toast";
import { useCallback, useState } from "react";
import { Meta } from "../../../../@types/paginated";
import {
  GamificationStatus,
  type IGamification,
  type IGamificationFilters,
} from "../../../../@types/gamification";
import { useCampaignsData } from "./hooks/useCampaignsData";
import { useDebouncedSearch } from "./hooks/useDebouncedSearch";
import { handeOrderBy } from "../../../../utils/pagination";
import { useGamificationFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { columns } from "./columns";
import Table from "../components/table";
import { EmptyState } from "../components/empty-state";
import { GamificationSettingsDialog } from "../components/gamification-settings-dialog";
import { GAMIFICATION_SETTINGS_COPY } from "../constants/messages";
import { AlertTriangle, Plus, RefreshCcw, Search, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SelectFilters from "./components/select-filters";

const SEARCH_DEBOUNCE_MS = 500;

function hasAnyFilter(filters: IGamificationFilters): boolean {
  return Boolean(
    filters.search ||
      filters.status ||
      filters.periodStart ||
      filters.periodEnd ||
      filters.campaignType,
  );
}

function buildQueryParams(filters: IGamificationFilters) {
  const includeDeleted = filters.status === GamificationStatus.INATIVA;
  return {
    search: filters.search || "",
    status: filters.status || "",
    orderBy: JSON.stringify(handeOrderBy(filters.order || {})),
    ...(filters.sortBy && { sortBy: filters.sortBy }),
    ...(filters.sortOrder && { sortOrder: filters.sortOrder }),
    ...(includeDeleted && { includeDeleted: true }),
    ...(filters.periodStart && { startDate: filters.periodStart }),
    ...(filters.periodEnd && { endDate: filters.periodEnd }),
    ...(filters.campaignType && { campaignType: filters.campaignType }),
    meta: {
      page: filters?.meta?.page || 1,
      perPage: filters?.meta?.perPage || 10,
    },
  };
}

interface ListContentProps {
  error: unknown;
  isLoading: boolean;
  campaigns: IGamification[];
  meta: Meta;
  filters: IGamificationFilters;
  isRefreshing: boolean;
  isFetching: boolean;
  updateFilter: (data: { key: string; value: unknown }) => void;
  onRefresh: () => void;
  onCreate: () => void;
  onResetFilters: () => void;
}

function ListContent({
  error,
  isLoading,
  campaigns,
  meta,
  filters,
  isRefreshing,
  isFetching,
  updateFilter,
  onRefresh,
  onCreate,
  onResetFilters,
}: ListContentProps) {
  if (error) {
    return (
      <div
        className="flex min-h-56 flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-center"
        role="alert"
      >
        <AlertTriangle aria-hidden="true" className="h-8 w-8 text-red-600" />
        <p className="max-w-lg text-sm leading-6 text-red-800">
          Não foi possível carregar as campanhas. Seus filtros foram mantidos —
          tente atualizar.
        </p>
        <Button
          variant="outline"
          onClick={onRefresh}
          disabled={isRefreshing || isFetching}
        >
          <RefreshCcw aria-hidden="true" className="h-4 w-4" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <TableLoad />;
  }

  if (campaigns.length > 0) {
    return (
      <Table
        data={campaigns}
        columns={columns}
        meta={meta}
        filters={filters}
        updateFilter={updateFilter}
      />
    );
  }

  if (hasAnyFilter(filters)) {
    return (
      <EmptyState
        variant="campaign"
        title="Nenhuma campanha corresponde aos filtros"
        description="Ajuste ou limpe os filtros para ver mais resultados."
        actionLabel="Limpar filtros"
        onAction={onResetFilters}
        showAction={true}
        actionIcon={<RefreshCcw aria-hidden="true" className="mr-2 h-4 w-4" />}
      />
    );
  }

  return (
    <EmptyState
      variant="campaign"
      title="Nenhuma campanha encontrada"
      description="Crie a primeira campanha para configurar a mecânica, os prêmios e o período de participação."
      actionLabel="Nova campanha"
      onAction={onCreate}
      showAction={true}
    />
  );
}

export const GamificationList: React.FC = () => {
  const { updateFilter, resetFilters } = useGamificationFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const { campaigns, meta, isLoading, isFetching, error, refetch } =
    useCampaignsData(buildQueryParams(filters));

  const applySearch = useCallback(
    (value: string): void => updateFilter({ key: "search", value }),
    [updateFilter],
  );
  const search = useDebouncedSearch(
    filters.search ?? "",
    applySearch,
    SEARCH_DEBOUNCE_MS,
  );

  const handleResetFilters = (): void => {
    search.cancelPending();
    resetFilters();
  };

  const busy = isLoading || isRefreshing || isFetching;

  const handleCreateCampaign = () => {
    navigate("/crm/gestao-gameficacao/criar");
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetch().unwrap();
      toast({
        title: "Atualizado",
        description: "Os dados da listagem foram atualizados com sucesso.",
      });
    } catch (refreshError) {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar a listagem. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <PageLayout>
      <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <PageTitle
          title="Gestão de Campanhas de Gameficação"
          description="Crie, acompanhe e atualize campanhas de gameficação"
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="min-h-11"
            onClick={handleRefresh}
            disabled={busy}
          >
            <RefreshCcw
              aria-hidden="true"
              className={`h-4 w-4 ${busy ? "animate-spin" : ""}`}
            />
            {busy ? "Atualizando..." : "Atualizar"}
          </Button>
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => setIsSettingsOpen(true)}
          >
            <Settings aria-hidden="true" className="mr-2 h-4 w-4" />
            {GAMIFICATION_SETTINGS_COPY.openAction}
          </Button>
          <Button className="min-h-11" onClick={handleCreateCampaign}>
            <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
            Nova campanha
          </Button>
        </div>
      </div>
      <ContentContainer>
        <div className="flex flex-col gap-4">
          <div className="grid w-full gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <div className="flex min-w-0 flex-col gap-2">
              <Label htmlFor="buscar-campanhas" className="text-sm">
                Buscar
              </Label>
              <div className="relative">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                />
                <Input
                  id="buscar-campanhas"
                  placeholder="Nome ou código"
                  value={search.text}
                  onChange={(event) => search.onTextChange(event.target.value)}
                  className="h-8 w-full pl-9 pr-3"
                />
              </div>
            </div>
            <SelectFilters />
          </div>

          <ListContent
            error={error}
            isLoading={isLoading}
            campaigns={campaigns}
            meta={meta as Meta}
            filters={filters}
            isRefreshing={isRefreshing}
            isFetching={isFetching}
            updateFilter={updateFilter}
            onRefresh={handleRefresh}
            onCreate={handleCreateCampaign}
            onResetFilters={handleResetFilters}
          />
        </div>
      </ContentContainer>
      <GamificationSettingsDialog
        open={isSettingsOpen}
        onOpenChange={setIsSettingsOpen}
      />
    </PageLayout>
  );
};
